import type { ReactNode } from 'react';
import { Icon } from '../ds';

/**
 * App shell — scroll area, bottom nav, and the FAB.
 *
 * The prototype drew a 392x812 phone with a bezel and a fake status bar. On a
 * real device the OS provides both, so the shell fills the viewport instead and
 * pads for the home indicator. On a wide screen it stays phone-width and
 * centred rather than stretching a layout designed for one thumb.
 */

export type ScreenKey = 'home' | 'log' | 'prep' | 'growth' | 'album';

const NAV: { key: ScreenKey; icon: string; label: string }[] = [
  { key: 'home', icon: 'House', label: 'ホーム' },
  { key: 'log', icon: 'Baby', label: 'きろく' },
  { key: 'prep', icon: 'Backpack', label: 'じゅんび' },
  { key: 'growth', icon: 'TrendingUp', label: 'せいちょう' },
  { key: 'album', icon: 'Image', label: 'アルバム' },
];

const NAV_HEIGHT = 66;

export function AppShell({
  screen,
  onNavigate,
  onAdd,
  children,
}: {
  screen: ScreenKey;
  onNavigate: (key: ScreenKey) => void;
  /** Omit to hide the FAB — the album has its own upload control. */
  onAdd?: () => void;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        position: 'relative',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 520,
        margin: '0 auto',
        background: 'var(--bg)',
        color: 'var(--text)',
        overflow: 'hidden',
      }}
    >
      <main className="scroll-area" style={{ flex: 1, minHeight: 0 }}>
        {children}
        {/* Clearance so the last row is never trapped under the floating FAB. */}
        <div style={{ height: onAdd ? 88 : 24 }} />
      </main>

      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label="記録を追加"
          style={{
            position: 'absolute',
            right: 18,
            bottom: `calc(${NAV_HEIGHT + 14}px + env(safe-area-inset-bottom, 0px))`,
            zIndex: 20,
            width: 58,
            height: 58,
            borderRadius: '50%',
            border: 'none',
            background: 'var(--brand)',
            color: 'var(--brand-on)',
            boxShadow: 'var(--shadow-lg)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="Plus" size={26} />
        </button>
      ) : null}

      <nav
        style={{
          flex: 'none',
          display: 'flex',
          height: NAV_HEIGHT,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxSizing: 'content-box',
          background: 'var(--surface)',
          borderTop: '1px solid var(--border)',
        }}
      >
        {NAV.map((item) => {
          const active = screen === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onNavigate(item.key)}
              aria-current={active ? 'page' : undefined}
              style={{
                flex: 1,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '8px 0 6px',
                color: active ? 'var(--brand)' : 'var(--text-subtle)',
              }}
            >
              <Icon name={item.icon} size={22} strokeWidth={active ? 2.4 : 2} />
              <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 600 }}>{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
